import {useUser} from '@gaia/stores/user';
import type {UnitSystem} from '@gaia/types';
import classes from '@map/Map/Map.module.css';
import {useEffect, useState} from 'react';

interface ScaleControlProps {
    map: mapboxgl.Map | undefined;
}

const DEFAULT_MAX_WIDTH = 200;

export function ScaleControl({map}: ScaleControlProps) {
    const [state, setState] = useState({
        width: `${DEFAULT_MAX_WIDTH}px`,
        label: '',
        distance: 0,
    });

    const {unitSystem: unit} = useUser();

    useEffect(() => {
        if (!map) return;

        setState(getScaleWidth({map, unit}));

        const onMove = () => setState(getScaleWidth({map, unit}));

        map.on('move', onMove);

        return () => {
            map.off('move', onMove);
        };
    }, [map, unit]);

    if (!map) return null;

    return (
        <div className={classes.scale}>
            <div style={{width: state.width}} className='mapboxgl-ctrl mapboxgl-ctrl-scale'>
                {state.distance} {state.label}
            </div>
        </div>
    );
}

interface GetScaleWidthProps {
    map: MapboxMap;
    unit: UnitSystem;
}

export function getScaleWidth({map, unit}: GetScaleWidthProps) {
    // A horizontal scale is imagined to be present at center of the map container with maximum
    // length. Using spherical law of cosines approximation, the real distance is found between
    // the two coordinates.
    const maxWidth = DEFAULT_MAX_WIDTH;
    const y = map.getContainer().clientHeight / 2;
    const left = map.unproject([0, y]);
    const right = map.unproject([maxWidth, y]);
    const maxMeters = left.distanceTo(right);
    // The real distance corresponding to maxWidth is rounded off to
    // near pretty number and the scale length for the same is found out.
    if (unit === 'imperial') {
        const maxFeet = 3.2808 * maxMeters;
        if (maxFeet > 5280) {
            const maxMiles = maxFeet / 5280;
            return {
                distance: getRoundNum(maxMiles),
                width: distanceToPx(maxMiles, maxWidth),
                label: 'mi',
            };
        } else {
            return {
                distance: getRoundNum(maxFeet),
                width: distanceToPx(maxFeet, maxWidth),
                label: 'ft',
            };
        }
    } else if (maxMeters >= 1000) {
        return {
            distance: getRoundNum(maxMeters / 1000),
            width: distanceToPx(maxMeters / 1000, maxWidth),
            label: 'km',
        };
    } else {
        return {
            distance: getRoundNum(maxMeters),
            width: distanceToPx(maxMeters, maxWidth),
            label: 'm',
        };
    }
}

function distanceToPx(d: number, maxWidth: number) {
    const distance = getRoundNum(d);
    const ratio = distance / d;
    return `${maxWidth * ratio}px`;
}

function getDecimalRoundNum(d: number) {
    const multiplier = Math.pow(10, Math.ceil(-Math.log(d) / Math.LN10));
    return Math.round(d * multiplier) / multiplier;
}

function getRoundNum(num: number) {
    const pow10 = Math.pow(10, `${Math.floor(num)}`.length - 1);
    let d = num / pow10;

    d = d >= 10 ? 10 : d >= 5 ? 5 : d >= 3 ? 3 : d >= 2 ? 2 : d >= 1 ? 1 : getDecimalRoundNum(d);

    return pow10 * d;
}
